package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.randomUuid

enum class RespostaTernaria(val rotulo: String) {
    SIM("Sim"),
    NAO("Não"),
    NA("N/A")
}

enum class RespostaSimNao(val rotulo: String) {
    SIM("Sim"),
    NAO("Não")
}

data class FotoAnexo(
    val id: String = randomUuid(),
    val path: String,
    val capturadoEm: Long,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /**
     * JPEG em Base64 (sem prefixo data:). Na Web, garante PDF/Excel mesmo se
     * IndexedDB/path falhar após reload — o JSON da inspeção carrega junto.
     */
    val conteudoBase64: String? = null
)

data class ItemVerificacao(
    val id: String,
    val titulo: String,
    val resposta: RespostaTernaria? = null,
    val fotos: List<FotoAnexo> = emptyList(),
    val justificativa: String = "",
    val obrigatorio: Boolean = true,
    val fotoObrigatoriaSeSim: Boolean = true,
    val fotoObrigatoriaSeNao: Boolean = true
)

data class SecaoSimNao(
    val resposta: RespostaSimNao? = null,
    val descricao: String = "",
    val fotos: List<FotoAnexo> = emptyList()
) {
    fun textoExportacao(): String {
        val resp = resposta?.rotulo ?: "-"
        return if (resposta == RespostaSimNao.SIM && descricao.isNotBlank()) "$resp — $descricao" else resp
    }
}

data class FormInspecao(
    val tstResponsavel: String = "",
    val dataHorarioMs: Long = currentTimeMillis(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val localTexto: String = "",
    val obra: String = "",
    val responsavelFrente: String = "",
    val descricaoAtividade: String = "",
    val efetivo: String = "",
    val itens: List<ItemVerificacao> = InspecaoCatalog.itensPadrao(),
    val desviosEncontrados: SecaoSimNao = SecaoSimNao(),
    val acoesCorretivas: SecaoSimNao = SecaoSimNao(),
    /** Campo opcional: texto livre + fotos (Inspeção de Campo Diária). */
    val informacoesAdicionais: String = "",
    val informacoesAdicionaisFotos: List<FotoAnexo> = emptyList(),
    val conclusao: String = "",
    val assinaturaSupervisor: String = "",
    val assinaturaSesmt: String = ""
) {
    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)

    fun linkGoogleMaps(): String? {
        val lat = latitude ?: return null
        val lng = longitude ?: return null
        return "https://maps.google.com/?q=$lat,$lng"
    }

    fun fotosParaExportacao(): List<Pair<String, FotoAnexo>> = buildList {
        itens.forEach { item ->
            item.fotos.forEachIndexed { index, foto ->
                add("${item.titulo} — Foto ${index + 1}" to foto)
            }
        }
        acoesCorretivas.fotos.forEachIndexed { index, foto ->
            add("Ações Corretivas — Foto ${index + 1}" to foto)
        }
        informacoesAdicionaisFotos.forEachIndexed { index, foto ->
            add("Informações adicionais — Foto ${index + 1}" to foto)
        }
    }
}

data class InspecaoCompleta(
    val id: Long = 0,
    val criadoEm: Long = currentTimeMillis(),
    val formTipo: FormTipo = FormTipo.INSPECAO_CAMPO,
    val form: FormInspecao = FormInspecao(),
    val relatorioFoto: FormRelatorioFoto? = null,
    val checklistAlojamento: FormChecklistAlojamento? = null,
    val comunicacaoOcorrencias: FormComunicacaoOcorrencias? = null
) {
    val rotuloTipo: String get() = formTipo.rotulo

    fun responsavelResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.tstResponsavel
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.responsavelAlojamento
                ?.ifBlank { checklistAlojamento?.inspecionadoPor?.nome.orEmpty() }
                .orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO ->
            checklistAlojamento?.inspecionadoPor?.nome.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.quemReportando.orEmpty()
    }

    fun obraResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.obra
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.obra?.ifBlank { checklistAlojamento?.obra.orEmpty() }.orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO -> checklistAlojamento?.obra.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS -> comunicacaoOcorrencias?.obra.orEmpty()
    }

    fun localResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.localTexto
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.endereco?.ifBlank { relatorioFoto.localTexto }
                ?.ifBlank {
                    checklistAlojamento?.endereco?.ifBlank { checklistAlojamento.localTexto }.orEmpty()
                }
                .orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO ->
            checklistAlojamento?.endereco?.ifBlank { checklistAlojamento.localTexto }.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.localExato
                ?.ifBlank { comunicacaoOcorrencias.localAcidente }
                ?.ifBlank { comunicacaoOcorrencias.localTexto }
                .orEmpty()
    }

    fun dataHorarioMsResumo(): Long = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.dataHorarioMs
        FormTipo.RELATORIO_FOTO_ALOJAMENTO ->
            relatorioFoto?.dataHorarioMs ?: criadoEm
        FormTipo.CHECKLIST_ALOJAMENTO ->
            checklistAlojamento?.dataHorarioMs ?: criadoEm
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.dataHorarioMs
                ?: checklistAlojamento?.dataHorarioMs
                ?: criadoEm
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.dataHorarioMs ?: criadoEm
    }
}

/** Remove JPEG Base64 embutido; mantém path/id/gps das fotos (gravadas à parte no IDB). */
fun FormInspecao.semConteudoBase64(): FormInspecao = copy(
    itens = itens.map { item ->
        item.copy(fotos = item.fotos.map { it.copy(conteudoBase64 = null) })
    },
    desviosEncontrados = desviosEncontrados.copy(
        fotos = desviosEncontrados.fotos.map { it.copy(conteudoBase64 = null) }
    ),
    acoesCorretivas = acoesCorretivas.copy(
        fotos = acoesCorretivas.fotos.map { it.copy(conteudoBase64 = null) }
    ),
    informacoesAdicionaisFotos = informacoesAdicionaisFotos.map { it.copy(conteudoBase64 = null) }
)
